import { prepareAssertions } from '~/packages/general/variable/prepareAssertions.util'
import { Variable } from '~/packages/general/variable/variables.typing'
import { healthVariableBaseRelevance } from '~/packages/general/variable/healthVariableRelevance.util'
import { defineAction } from '~/prix'
import expression from '~/prix/types/expression'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { spaceForPoint } from '~/prix/types/presets/space'
import { timeForCurrentOnly } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import { getZodSchemaInfo } from '~/prix/types/zodType'
import { z } from 'zod'
import citizenStatsAction from './citizenStats.action.json' with { type: 'json' }
import { citizenLocationShape, citizenShapeBase } from './citizenShape'

export const citizenShape = z.object({
  ...citizenShapeBase.shape,
  ...citizenLocationShape.shape,
})

export const citizenExpression = expression(
  citizenShape.pick({
    sex: true,
    age: true,
    ageDecimal: true,
    ageRange: true,
    deceased: true,
    healthUnitCnes: true,
    teamIne: true,
  }).shape,
)

export const citizenVariable: Variable = {
  key: 'citizen',
  actionDefinitionKey: citizenStatsAction.key,
  title: 'Cidadão',
  categoryKey: 'healthCitizens',
  unitSingular: 'cidadão',
  unitPlural: 'cidadãos',
  grammaticalGender: 'male',
  timeType: 'currentOnly',
  shape: getZodSchemaInfo(citizenShape),
  filterableBy: getZodSchemaInfo(
    citizenShape.pick({
      sex: true,
      ageRange: true,
      deceased: true,
      healthUnitCnes: true,
      teamIne: true,
    }),
  ),
  betterDensity: 'more',
  relevance: healthVariableBaseRelevance.citizen,
  basicSelect: ['name', 'socialName', 'birthDate', 'sex'],
  detailedSelect: [
    'cns',
    'cpf',
    'phoneNumber',
    'sex',
    'birthDate',
    'genderIdentity',
    'deceased',
    'healthUnitCnes',
    'teamIne',
    'latitude',
    'longitude',
    'angleFromStreet',
    'streetName',
    'streetNumber',
    'streetType',
    'complement',
    'referencePoint',
    'postalCode',
    'neighborhood',
    'municipality',
    'state',
    'deathDate',
    'createdAt',
    'updatedAt',
  ],
  assertions: prepareAssertions(citizenExpression),
  timeSeries: {
    fitDays: 365 * 10,
    layers: [
      {
        layer: 'calendarSeasonality',
        type: 'monthOfYear',
        degree: 1,
        optimization: {
          minimumDays: 1095,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
      {
        layer: 'trend',
        degree: 2,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfWeek',
        degree: 1,
        optimization: {
          minimumDays: 60,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
      {
        layer: 'calendarSeasonality',
        type: 'holiday',
        degree: 1,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfMonth',
        degree: 1,
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
    ],
  },
}

const citizenStatsDefinition = defineAction({
  ...citizenStatsAction,
  input: z.object({
    variable,
    highlight: citizenExpression.optional(),
    filter: citizenExpression.optional(),
    space: spaceForPoint().optional(),
    time: timeForCurrentOnly().optional(),
    select: select(citizenShape),
    assertions,
    analysis,
  }),
  output: z.any(),
  cache: input => {
    if (input.filter || input.space?.delimitationType === 'custom') {
      return {}
    }
    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
    }
  },
})

export default citizenStatsDefinition
